// kernel/dailyCreditUsageAlert.ts
// Pure part of the daily credit usage alert preference write: normalization, validation and
// merge into the sibling notification preferences. Kept out of React so the rules a user hits
// (a positive threshold is mandatory once the alert is on, a time zone must be a real IANA
// zone) are testable without a Kernel or a DOM.

import type {
  DailyCreditUsageAlertBuildResult,
  DailyCreditUsageNotificationPreference,
  UserPreferences,
} from '#types/types';

export const normalizePositiveInteger = (value: number | null | undefined): number | null => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  const rounded = Math.round(value);
  return rounded > 0 ? rounded : null;
};

const isValidTimeZone = (value: string): boolean => {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value });
    return true;
  } catch {
    return false;
  }
};

/**
 * Builds the next `notificationPreferences` value for the daily credit usage alert.
 * `fallbackTimezone` is a browser value: the caller resolves it and hands it over.
 */
export const buildDailyCreditUsageAlertPreferences = (
  alert: DailyCreditUsageNotificationPreference,
  currentPreferences: UserPreferences,
  fallbackTimezone: string
): DailyCreditUsageAlertBuildResult => {
  const nextEnabled = alert.enabled === true;
  const nextThreshold = normalizePositiveInteger(alert.thresholdCredits);
  const nextRepeatStep = normalizePositiveInteger(alert.repeatStepCredits);
  const nextTimezone = alert.timezone?.trim() || fallbackTimezone;

  if (nextEnabled && !nextThreshold) {
    return { ok: false, error: 'Set a positive daily credit threshold before enabling this alert.' };
  }
  if (!isValidTimeZone(nextTimezone)) {
    return { ok: false, error: 'Use a valid standard time zone, for example Europe/Paris.' };
  }

  return {
    ok: true,
    notificationPreferences: {
      ...currentPreferences.notificationPreferences,
      dailyCreditUsageAlert: {
        enabled: nextEnabled,
        thresholdCredits: nextThreshold,
        repeatStepCredits: nextRepeatStep,
        timezone: nextTimezone,
      },
    },
  };
};
